import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Send, ShieldAlert, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";

const STATUS_LABEL = {
  SUBMITTED_TO_PROVIDER: "Transmis au prestataire",
  ACCEPTED_BY_PROVIDER: "Pris en charge",
  PRINTING: "Impression en cours",
  HANDED_TO_POSTAL_NETWORK: "Remis au réseau postal",
  IN_TRANSIT: "En cours d'acheminement",
  DELIVERED: "Distribué",
  DELIVERED_WITH_SIGNATURE: "Distribué avec signature (AR)",
  RETURNED_NPAI: "Retour NPAI",
  POSTAL_FAILED: "Échec — intervention requise",
  PAID_AWAITING_FULFILLMENT: "En attente d'envoi (admin)",
};

const fmtEur = (c) => `${(Number(c || 0) / 100).toFixed(2)} €`;

export default function AdminMysendingboxPage() {
  const [status, setStatus] = useState(null);
  const [conn, setConn] = useState(null);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingCase, setPendingCase] = useState(null); // for confirmation dialog
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get("/admin/postal/mysendingbox/status"),
        api.get("/admin/cases?limit=500"),
      ]);
      setStatus(s.data);
      const items = Array.isArray(c.data) ? c.data : (c.data?.items || []);
      setCases(items.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (e) {
      toast.error("Chargement impossible");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const testConnection = async () => {
    setConn({ loading: true });
    try {
      const { data } = await api.post("/admin/postal/mysendingbox/test-connection");
      setConn(data);
      toast[data.ok ? "success" : "error"](data.ok ? "MySendingBox répond ✓" : "Connexion refusée");
    } catch (e) {
      setConn({ ok: false, error: e?.response?.data?.detail || "Erreur" });
      toast.error("Impossible de tester la connexion");
    }
  };

  const confirmSend = async () => {
    if (!pendingCase) return;
    setSending(true);
    try {
      const { data } = await api.post(
        `/admin/postal/mysendingbox/send/${pendingCase.id}`,
        { confirmation: true }
      );
      if (data.already_sent) {
        toast.info("Un envoi existait déjà pour ce dossier.");
      } else {
        toast.success(`Courrier transmis — ID ${data.provider_letter_id?.slice(0, 12) || "?"}`);
      }
      setPendingCase(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi refusé");
    } finally { setSending(false); }
  };

  const eligibleCases = cases.filter((c) =>
    c.status === "paid" &&
    (c.postal_status === "PAID_AWAITING_FULFILLMENT" || !c.postal_status) &&
    !c.postal_shipment_id &&
    c.offer !== "download"
  );
  const trackedCases = cases.filter((c) => c.postal_shipment_id);

  return (
    <div data-testid="admin-msb-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Envois postaux MySendingBox</h1>
          <p className="text-sm text-muted-foreground mt-1">Déclenchement manuel des lettres recommandées AR (LRAR).</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="msb-refresh-btn">
          <RefreshCw className="h-3 w-3 mr-1" /> Actualiser
        </Button>
      </div>

      {/* Config card */}
      <div className="card-elevated p-5 mb-6" data-testid="msb-status-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`h-2 w-2 rounded-full ${status?.configured ? "bg-green-500" : "bg-red-500"}`} />
            <div className="font-semibold">MySendingBox — {status?.environment?.toUpperCase() || "?"}</div>
          </div>
          <Button variant="outline" size="sm" onClick={testConnection} data-testid="msb-test-conn-btn">
            Tester la connexion
          </Button>
        </div>
        {status && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
            <Info label="Configurée" value={status.configured ? "oui" : "non"} />
            <Info label="Environnement" value={status.environment} highlight={status.environment === "live"} />
            <Info label="Base URL" value={status.base_url} />
            <Info label="Auth" value={status.auth_scheme} />
            <Info label="Produit postal" value={status.product_code} />
            <Info label="Clé (masquée)" value={status.key_hint || "—"} mono />
            <Info label="Webhook URL" value={status.webhook_url || "—"} />
            <Info label="Token webhook" value={status.webhook_token_configured ? "configuré" : "manquant"} />
            {conn && !conn.loading && (
              <Info label="Test connexion" value={conn.ok ? `OK (${conn.status_code})` : `Échec — ${conn.error || conn.status_code}`} highlight={conn.ok} />
            )}
          </div>
        )}
      </div>

      {/* Eligible cases */}
      <div className="card-elevated p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Send className="h-4 w-4 text-primary" />
          <h2 className="text-lg font-semibold">Dossiers à envoyer ({eligibleCases.length})</h2>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-muted-foreground text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
          </div>
        ) : eligibleCases.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4">Aucun dossier payé en attente d&apos;envoi.</div>
        ) : (
          <div className="divide-y divide-border">
            {eligibleCases.map((c) => (
              <div key={c.id} className="py-3 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-mono text-xs text-muted-foreground">{c.id.slice(0, 12)}</div>
                  <div className="font-medium truncate">{c.organization_name || "—"}</div>
                  <div className="text-xs text-muted-foreground">Offre: {c.offer} · {fmtEur(c.price_cents)}</div>
                </div>
                <Button
                  size="sm"
                  className="bg-gradient-brand text-white border-0"
                  onClick={() => setPendingCase(c)}
                  data-testid={`msb-send-btn-${c.id.slice(0, 8)}`}
                >
                  <Send className="h-3 w-3 mr-1" /> Envoyer avec MySendingBox
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tracked shipments */}
      <div className="card-elevated p-5">
        <div className="flex items-center gap-2 mb-4">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <h2 className="text-lg font-semibold">Envois en cours de suivi ({trackedCases.length})</h2>
        </div>
        {trackedCases.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4">Aucun envoi actif.</div>
        ) : (
          <div className="text-sm">
            <table className="w-full">
              <thead className="text-left text-xs text-muted-foreground uppercase">
                <tr>
                  <th className="py-2">Dossier</th>
                  <th className="py-2">Organisme</th>
                  <th className="py-2">Statut postal</th>
                  <th className="py-2">Tracking</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {trackedCases.slice(0, 30).map((c) => (
                  <tr key={c.id}>
                    <td className="py-2 font-mono text-xs">{c.id.slice(0, 12)}</td>
                    <td className="py-2">{c.organization_name || "—"}</td>
                    <td className="py-2"><Badge variant="outline">{STATUS_LABEL[c.postal_status] || c.postal_status || "—"}</Badge></td>
                    <td className="py-2 font-mono text-xs">{c.tracking_number || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirmation dialog */}
      <Dialog open={!!pendingCase} onOpenChange={(o) => !o && setPendingCase(null)}>
        <DialogContent data-testid="msb-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-orange-500" />
              Confirmer l&apos;envoi réel
            </DialogTitle>
            <DialogDescription className="pt-2 leading-relaxed">
              Vous allez déclencher l&apos;impression, la mise sous pli, l&apos;affranchissement et l&apos;envoi réel de ce courrier
              (Lettre Recommandée avec AR). Cette action sera facturée par MySendingBox et ne doit pas être répétée.
            </DialogDescription>
          </DialogHeader>
          {pendingCase && (
            <div className="rounded-md border border-border p-4 text-sm space-y-1">
              <div><span className="text-muted-foreground">Dossier :</span> <span className="font-mono">{pendingCase.id.slice(0, 12)}</span></div>
              <div><span className="text-muted-foreground">Destinataire :</span> <b>{pendingCase.organization_name}</b></div>
              <div><span className="text-muted-foreground">Offre :</span> {pendingCase.offer} — {fmtEur(pendingCase.price_cents)}</div>
              <div className="flex items-center gap-2 text-orange-700 mt-2">
                <AlertTriangle className="h-3 w-3" />
                <span className="text-xs">Environnement : <b>LIVE</b> — courrier physique réel</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingCase(null)} disabled={sending} data-testid="msb-cancel-btn">
              Annuler
            </Button>
            <Button
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={confirmSend}
              disabled={sending}
              data-testid="msb-confirm-send-btn"
            >
              {sending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
              Confirmer l&apos;envoi réel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value, mono, highlight }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
      <span className={`${mono ? "font-mono" : ""} ${highlight ? "text-green-700 font-semibold" : ""}`}>{String(value)}</span>
    </div>
  );
}
